import { useInfiniteQuery } from "@tanstack/react-query";
import type { components } from "../../api/schema";
import { apiClient } from "../../lib/apiClient";
import {
  cellParam,
  formatRate,
  parseCellParam,
} from "../../lib/alignmentDrill";
import { ApiRequestError, requestErrorDescription } from "../../lib/errors";
import { markerScoreRequest } from "../../lib/markerReview";
import { queryKeys } from "../../lib/queryKeys";
import { reviewFeedFilters } from "../../lib/reviewFeed";
import type { AlignmentCellParam } from "../../lib/routerSearch";
import { shortResourceId } from "../../lib/shellNavigation";
import {
  Button,
  ConfusionMatrix,
  DataTable,
  InlineError,
  KeyValue,
  Skeleton,
  Stack,
  Text,
} from "../../ui";

const flaggedReasonLabels = new Map([
  ["ambiguous", "Ambiguous"],
  ["low_quality", "Low quality"],
  ["off_topic", "Off topic"],
]);

type Disagreement = components["schemas"]["AlignmentDisagreement"];

export function ReviewMarkerScorecard({
  summary,
  enabled,
  markerId,
  feed,
  cell,
  onCell,
  onOpen,
}: {
  summary?: Pick<
    components["schemas"]["MarkerReviewSummaryItem"],
    "flagged_count" | "flagged_reason_counts"
  >;
  enabled: boolean;
  markerId: string;
  feed: string;
  cell?: AlignmentCellParam;
  onCell: (cell?: AlignmentCellParam) => void;
  onOpen: (row: Disagreement) => void;
}) {
  const body = markerScoreRequest(reviewFeedFilters(feed), cell);
  const score = useInfiniteQuery({
    enabled,
    queryKey: queryKeys.alignment.list(body),
    initialPageParam: undefined as string | undefined,
    queryFn: async ({ pageParam, signal }) => {
      const { data, error } = await apiClient.POST("/v1/alignment:score", {
        signal,
        body: { ...body, page_token: pageParam },
      });
      if (error) throw new ApiRequestError(error);
      return data;
    },
    getNextPageParam: (page) => page.next_page_token ?? undefined,
  });
  const response = score.data?.pages[0];
  const card = response?.by_marker?.find((row) => row.marker_id === markerId);
  const selected = parseCellParam(cell);
  const rows =
    score.data?.pages.flatMap((page) => page.disagreements ?? []) ?? [];
  const missed =
    card?.confusion?.reduce(
      (count, entry) =>
        count +
        (entry.human === "fail" &&
        (entry.machine === "pass" || entry.machine === "warn")
          ? entry.count
          : 0),
      0,
    ) ?? 0;
  const expected =
    card?.confusion?.reduce(
      (count, entry) =>
        count +
        (selected
          ? entry.human === selected.human && entry.machine === selected.machine
            ? entry.count
            : 0
          : entry.human !== entry.machine
            ? entry.count
            : 0),
      0,
    ) ?? 0;
  return (
    <Stack gap={4}>
      {summary ? (
        <Stack gap={2}>
          <KeyValue
            rows={[
              { label: "Flagged calls", value: String(summary.flagged_count) },
            ]}
          />
          <KeyValue
            rows={Object.entries(summary.flagged_reason_counts)
              .sort(([left], [right]) => left.localeCompare(right))
              .map(([reason, count]) => ({
                label: flaggedReasonLabels.get(reason) ?? reason,
                value: String(count),
              }))}
          />
        </Stack>
      ) : null}
      {score.isError ? (
        <InlineError>{requestErrorDescription(score.error)}</InlineError>
      ) : null}
      {enabled && score.isPending ? (
        <Skeleton shape="detail-field" label="Loading scorecard" rows={4} />
      ) : null}
      {response?.notice ? <Text variant="muted">{response.notice}</Text> : null}
      {card ? (
        <>
          <KeyValue
            rows={[
              { label: "Agreement", value: formatRate(card.agreement) },
              { label: "Missed failures", value: String(missed) },
            ]}
          />
          <Text variant="muted">{String(card.sample_size)} compared calls</Text>
          <ConfusionMatrix
            cells={card.confusion ?? []}
            total={card.sample_size}
            selected={selected}
            onSelect={(next) => {
              onCell(cellParam(next));
            }}
          />
          {selected ? (
            <>
              <Text>
                Human {selected.human} · Machine {selected.machine}
              </Text>
              <Button
                size="sm"
                variant="ghost"
                onClick={() => {
                  onCell();
                }}
              >
                Clear cell
              </Button>
            </>
          ) : null}
          <Text variant="muted">
            {String(rows.length)} of {String(expected)} disagreements
          </Text>
          <DataTable
            ariaLabel="Marker disagreements"
            columns={[
              {
                header: "Call",
                cell: ({ row }) => shortResourceId(row.original.transcript_id),
              },
              { header: "Human", accessorKey: "human_verdict" },
              { header: "Machine", accessorKey: "machine_verdict" },
            ]}
            data={rows}
            getRowId={(row) => row.transcript_id}
            onRowClick={(row) => {
              onOpen(row.original);
            }}
            emptyMessage="No disagreements in the loaded page"
          />
          {score.hasNextPage ? (
            <Button
              variant="ghost"
              disabled={score.isFetchingNextPage}
              onClick={() => {
                void score.fetchNextPage();
              }}
            >
              Load more disagreements
            </Button>
          ) : null}
        </>
      ) : response ? (
        <Text>No comparable Labels and Marks</Text>
      ) : null}
    </Stack>
  );
}
